import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "@i18n";
import { CircularProgress, MenuItem, Select } from "@mui/material";
import _ from "lodash";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@Reducers/index";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { jiraTicketsUrl, jiraController } from "../api/jira.api";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { convertDate } from "@Services/luxonHelper";
import { exportPdf, EXPORT_TYPE } from "@Services/fileDownload";
import { convertHtmlToAdf, getReportedFilter, mutedStatusColors } from "./constants";
import { getJiraStatusHex, jiraIdMapper } from "@Helpers";
import ChangeTicketTypeModal, { ChangeTypePayload } from "./ChangeTicketTypeModal";
import IconComponent from "@CubeComponents/icons";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import SplitButton, { type SplitButtonOption } from "@CubeComponents/html/SplitButtonComponent";
import { getAllNotificationPageUrl } from "@Services/utils/helpers";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type {
  WhiteListUsersResponse,
  GetUsersForTicketCreationResponse,
  UserForTicketCreation,
} from "@ServerTypes/jira/jira.schema";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import useJiraTickets, { type CreateTicketMenuItem } from "./useJiraTickets";
import Text from "@CubeComponents/html/Text/Text";
import Box from "@mui/material/Box";
import ToolTipComponent from "@CubeComponents/html/tooltip";

import {
  CreateOnBehalfComponent,
  SelectUserComponent,
  SelectPortalComponent,
  SummaryComponent,
  DescriptionComponent,
  AttachmentComponent,
  PriorityComponent,
  ImpactComponent,
  LinkUrlComponent,
  VisibilityComponent,
} from "./JiraTicketFields";

const JiraTickets = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userDetails, portalData, portalSettingInfo } = useSelector((state: RootState) => state);

  const [searchParams] = useSearchParams();
  const section = searchParams.get("supportType")?.toUpperCase() || null;
  const createdBy = searchParams.get("createdBy") || "createdByMe";
  const referrerUrl = searchParams.get("referrerUrl") || "";

  const dataGrid = useRef<any>(null);

  const [columnDefinitions, setColumnDefinitions] = useState<any>([]);
  const [whiteListUsersForJiraTickets, setWhiteListUsersForJiraTickets] = useState<string[] | null>(null);
  const [allUsersForTicketCreation, setAllUsersForTicketCreation] = useState<UserForTicketCreation[]>([]);
  const [createdByFilter, setCreatedByFilter] = useState<string>(createdBy);
  const [query, setQuery] = useState<any>({
    action: "getList",
    createdBy,
    requiresCounts: true,
  });
  const [viewTrigger, setViewTrigger] = useState<Record<string, unknown> | false>(false);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [showChangeTypeModal, setShowChangeTypeModal] = useState<boolean>(false);
  const [changeTypeLoading, setChangeTypeLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const exportingRef = useRef<boolean>(false);

  const {
    ticketCreationFields,
    isCurrentUserWhiteListed,
    setIsCurrentUserWhiteListed,
    showTicketCreationForm,
    setShowTicketCreationForm,
    handleSaveBugDetails,
    showCreationLoader,
    closeTicketCreationModal,
    isDisabledSaveButton,
    ticketHeaderInfo,
    createTicketMenuItems,
  } = useJiraTickets({
    allUsers: allUsersForTicketCreation,
    convertHtmlToAdf,
    referrerUrl,
    section,
    onSaveSucessfull: () => {
      setTimeout(() => {
        refreshGrid();
      }, 1000);
    },
    closeTicketCreationModalCallback: (section: any) => {
      if (section) {
        navigate(getAllNotificationPageUrl({ section: "support" }));
      }
    },
    component: {
      createOnBehalfComopnent: CreateOnBehalfComponent,
      summaryComponent: SummaryComponent,
      descriptionComponent: DescriptionComponent,
      priorityComponent: PriorityComponent,
      impactComponent: ImpactComponent,
      linkUrlComponent: LinkUrlComponent,
      visibilityComponent: VisibilityComponent,
      selectUserComponent: SelectUserComponent,
      selectPortalComponent: SelectPortalComponent,
      attachmentComponent: AttachmentComponent,
    },
  });

  const fetchUsersForTicketCreation = useCallback(async () => {
    const response = await axiosWrapper.get<GetUsersForTicketCreationResponse>(
      `${getBackendUrl()}/jira/getUsersForTicketCreation`,
    );

    if (response?.data?.status) {
      const filteredUsers = response.data.result?.filter((user) => user._id !== userDetails._id) ?? [];
      setAllUsersForTicketCreation(filteredUsers);
    }
  }, [userDetails._id]);

  const fetchWhiteListUsers = async () => {
    const res = await axiosWrapper.get<WhiteListUsersResponse>(`${getBackendUrl()}/jira/whiteListUsers`);
    setWhiteListUsersForJiraTickets(res.data.users ?? []);
  };

  useEffect(() => {
    fetchWhiteListUsers();
  }, []);

  useEffect(() => {
    if (Object.keys(userDetails).length && whiteListUsersForJiraTickets !== null) {
      const isWhitelisted = whiteListUsersForJiraTickets.includes(userDetails?.email ?? "");
      setIsCurrentUserWhiteListed(isWhitelisted);
    }
  }, [userDetails, whiteListUsersForJiraTickets, setIsCurrentUserWhiteListed]);

  useEffect(() => {
    if (isCurrentUserWhiteListed) {
      fetchUsersForTicketCreation();
    }
  }, [isCurrentUserWhiteListed, fetchUsersForTicketCreation]);

  useEffect(() => {
    if (!Object.keys(userDetails).length || whiteListUsersForJiraTickets === null) return;

    const isWhitelisted = whiteListUsersForJiraTickets.includes(userDetails?.email ?? "");

    const isBugRow = (row: any) => _.get(row, "fields.issueType.id") === jiraIdMapper["Report a Bug"];
    const priorityLabel = (row: any) => (isBugRow(row) ? _.get(row, "fields.priority.label") : "");
    const slaClock = (row: any, variant: "response" | "resolution") =>
      isBugRow(row) ? _.get(row, `fields.sla.${variant}`) : undefined;
    const slaInstant = (ms: any) => (ms ? String(convertDate(ms, "dateForPdfExport")) : "-");
    const slaMet = (row: any, variant: "response" | "resolution") => {
      const clock: any = slaClock(row, variant);
      if (!clock) return "-";
      if (clock.actualAt != null) return clock.metInTime ? String(t("Yes")) : String(t("No"));
      if (clock.state === "breachedOpen") return String(t("Breached"));
      return String(t("Pending"));
    };
    const slaColumnCommon = {
      sortable: false,
      disableSorting: true,
      disableFiltering: true,
      disableGrouping: true,
      initiallyHidden: true,
    };
    const slaColumns = [
      {
        ...slaColumnCommon,
        id: "priority",
        field: "fields.priority.label",
        name: t("Priority"),
        minWidth: 110,
        formatter: (data: any, params: any, a: any, b: any, value: any) => priorityLabel(value) || "-",
        _valueGetter: (args: any) => priorityLabel(args) || "",
        _excelValueGetter: (row: any) => priorityLabel(row) || "-",
      },
      {
        ...slaColumnCommon,
        id: "firstResponseDeadline",
        field: "fields.sla.response.deadlineAt",
        name: t("First Response SLA Deadline"),
        minWidth: 200,
        formatter: (data: any, params: any, a: any, b: any, value: any) =>
          slaInstant(slaClock(value, "response")?.deadlineAt),
        _valueGetter: (args: any) => slaClock(args, "response")?.deadlineAt ?? null,
        _excelValueGetter: (row: any) => slaInstant(slaClock(row, "response")?.deadlineAt),
      },
      {
        ...slaColumnCommon,
        id: "actualResponse",
        field: "fields.sla.response.actualAt",
        name: t("Actual Response"),
        minWidth: 180,
        formatter: (data: any, params: any, a: any, b: any, value: any) =>
          slaInstant(slaClock(value, "response")?.actualAt),
        _valueGetter: (args: any) => slaClock(args, "response")?.actualAt ?? null,
        _excelValueGetter: (row: any) => slaInstant(slaClock(row, "response")?.actualAt),
      },
      {
        ...slaColumnCommon,
        id: "responseMet",
        field: "fields.sla.response.metInTime",
        name: t("Response Met"),
        minWidth: 130,
        formatter: (data: any, params: any, a: any, b: any, value: any) => slaMet(value, "response"),
        _valueGetter: (args: any) => slaMet(args, "response"),
        _excelValueGetter: (row: any) => slaMet(row, "response"),
      },
      {
        ...slaColumnCommon,
        id: "resolutionDeadline",
        field: "fields.sla.resolution.deadlineAt",
        name: t("Resolution SLA Deadline"),
        minWidth: 200,
        formatter: (data: any, params: any, a: any, b: any, value: any) =>
          slaInstant(slaClock(value, "resolution")?.deadlineAt),
        _valueGetter: (args: any) => slaClock(args, "resolution")?.deadlineAt ?? null,
        _excelValueGetter: (row: any) => slaInstant(slaClock(row, "resolution")?.deadlineAt),
      },
      {
        ...slaColumnCommon,
        id: "actualResolution",
        field: "fields.sla.resolution.actualAt",
        name: t("Actual Resolution"),
        minWidth: 180,
        formatter: (data: any, params: any, a: any, b: any, value: any) =>
          slaInstant(slaClock(value, "resolution")?.actualAt),
        _valueGetter: (args: any) => slaClock(args, "resolution")?.actualAt ?? null,
        _excelValueGetter: (row: any) => slaInstant(slaClock(row, "resolution")?.actualAt),
      },
      {
        ...slaColumnCommon,
        id: "resolutionMet",
        field: "fields.sla.resolution.metInTime",
        name: t("Resolution Met"),
        minWidth: 130,
        formatter: (data: any, params: any, a: any, b: any, value: any) => slaMet(value, "resolution"),
        _valueGetter: (args: any) => slaMet(args, "resolution"),
        _excelValueGetter: (row: any) => slaMet(row, "resolution"),
      },
    ];

    const baseColumns = [
      {
        id: "type",
        field: "fields.issueType.label",
        name: t("Type"),
        minWidth: 150,
        filterType: "Text",
        formatter: (data: number, params: number, a: any, b: unknown, value: any) => typeTemplate(value),
        _valueGetter: (args) => _.get(args, "fields.issueType.label"),
      },
      {
        id: "jiraTicketKey",
        field: "jiraTicketKey",
        name: t("ID"),
        filterType: "Text",
        minWidth: 100,
      },
      {
        id: "summary",
        field: "fields.summary",
        name: t("Summary"),
        filterType: "Text",
        minWidth: 250,
        formatter: (data: number, params: number, a: any, b: unknown, value: any) => _.get(value, "fields.summary"),
        _valueGetter: (args) => _.get(args, "fields.summary"),
        wrapText: true,
      },
      ...(isWhitelisted
        ? [
            {
              id: "portalName",
              field: "portal.name",
              name: t("Portal Name"),
              filterType: "Text",
              formatter: (data: number, params: number, a: any, b: unknown, value: any) => portalNameTemplate(value),
              minWidth: 250,
              _valueGetter: (args) => _.get(args, "portal.name"),
            },
            {
              id: "organizationName",
              field: "organization.name",
              name: t("Organization"),
              filterType: "Text",
              minWidth: 250,
              formatter: (data: number, params: number, a: any, b: unknown, value: any) =>
                value?.organization?.name || "-",
              _valueGetter: (args) => _.get(args, "organization.name"),
            },
            {
              id: "source",
              field: "source",
              name: t("Source"),
              filterType: "Text",
              formatter: (data: number, params: number, a: any, b: unknown, value: any) => sourceTemplate(value),
              minWidth: 150,
            },
          ]
        : []),
      {
        id: "createdBy",
        field: "createdBy.fullName",
        name: t("Created By"),
        filterType: "Text",
        formatter: (data: number, params: number, a: any, b: unknown, value: any) => createdByTemplate(value),
        _valueGetter: (args: any) => _.get(args, "createdBy.fullName"),
        minWidth: 150,
      },
      {
        id: "createdOn",
        field: "createdOn",
        name: t("Created On"),
        filterType: "Date",
        formatter: (data: number, params: number, a: any, b: unknown, value: any) => createdOnTemplate(value),
        _excelValueGetter: (row: any) => String(convertDate(_.get(row, "createdOn"), "dateForPdfExport")),
        minWidth: 150,
      },
      {
        id: "status",
        field: "fields.issueStatus.label",
        name: t("Status"),
        filterType: "Text",
        minWidth: 200,
        formatter: (data: number, params: number, a: any, b: unknown, value: any) => statusTemplate(value),
        _valueGetter: (args) => _.get(args, "fields.issueStatus.label"),
      },
      ...(isWhitelisted ? slaColumns : []),
    ];

    setColumnDefinitions(baseColumns);
  }, [userDetails, whiteListUsersForJiraTickets]);

  useEffect(() => {
    if (portalData?._id && !createdByFilter) {
      setCreatedByFilter(createdBy);
    }
  }, [portalData, createdByFilter, createdBy]);

  const typeTemplate = (args: any) => {
    let imgSrc;
    if (args.fields.issueType.id === "10012") {
      imgSrc = "suggestAFeature";
    } else if (args.fields.issueType.id === "10013") {
      imgSrc = "bugReport";
    } else {
      imgSrc = "askAQuestion";
    }
    const container = document.createElement("div");
    const root = createRoot(container);

    root.render(
      <Box className="flex gap-2 items-center">
        <IconComponent name={imgSrc} width="1.25rem" height="1.25rem" />
        <span>{args.fields.issueType.label}</span>
      </Box>,
    );
    return container;
  };

  const portalNameTemplate = (args: any) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    const portalName = args?.portal?.name;
    const frontendUrl = args?.portal?.frontendUrl;
    const displayText = portalName && frontendUrl ? `${portalName} (${frontendUrl})` : "-";
    root.render(<Text>{displayText}</Text>);
    return container;
  };

  const statusTemplate = (value: any) => {
    const { bg, border } = mutedStatusColors(
      getJiraStatusHex(_.get(value, "fields.issueStatus.id"), _.get(value, "fields.issueStatusCategory.id")),
    );
    const container = document.createElement("div");
    const root = createRoot(container);

    root.render(
      <Box
        sx={{ backgroundColor: bg, color: "#192048", border: `1px solid ${border}` }}
        className="font-semibold px-4 py-2 rounded whitespace-nowrap w-fit items-center"
      >
        {value.fields.issueStatus.label}
      </Box>,
    );

    return container;
  };

  const createdByTemplate = (value: any) => {
    const container = document.createElement("div");
    renderUserTag(container, {
      id: _.get(value, "createdBy._id"),
      fullName: _.get(value, "createdBy.fullName"),
      email: _.get(value, "createdBy.email"),
    });
    return container;
  };

  const createdOnTemplate = (value: any) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <ToolTipComponent position="bottom" content={convertDate(_.get(value, "createdOn"), "dateForTooltip")}>
        {convertDate(_.get(value, "createdOn"), "dateForText")}
      </ToolTipComponent>,
    );
    return container;
  };

  const sourceTemplate = (value: any) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(<Text>{value?.source}</Text>);
    return container;
  };

  const refreshGrid = useCallback(() => {
    setQuery({
      action: "getList",
      createdBy: createdByFilter,
      requiresCounts: true,
    });
  }, [createdByFilter]);

  const handleClickLayout = (params: any) => {
    const saved = params?.queryConditions?.createdBy;
    if (!saved || saved === createdByFilter) return;
    searchParams.set("createdBy", saved);
    setCreatedByFilter(saved);
    setQuery({ action: "getList", createdBy: saved, requiresCounts: true });
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };
  const canRaiseSupportTicket = isCurrentUserWhiteListed || !!portalSettingInfo?.raiseSupportTicket?.enable;

  const createTicketOptions: SplitButtonOption[] = createTicketMenuItems.map((item) => ({
    id: item.id,
    optionDisplay: (
      <Box className="flex gap-2 items-center">
        <IconComponent name={item.iconName} width="1.25rem" height="1.25rem" />
        <Text>{item.text}</Text>
      </Box>
    ),
    buttonDisplay: (
      <Box className="flex gap-2 items-center">
        <IconComponent name={item.iconName} width="1rem" height="1rem" />
        <Text>{item.text}</Text>
      </Box>
    ),
  }));

  const openTicketCreationForm = (option: SplitButtonOption) => {
    const id = typeof option === "string" ? option : option.id;
    if (!id) return;
    setShowTicketCreationForm({ type: String(id), status: true });
  };

  const createTicketDropdown = () => (
    <Box className="ms-4" data-testid="createBtn">
      <SplitButton
        options={createTicketOptions}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        disabled={!canRaiseSupportTicket}
        onClick={(_e, option) => openTicketCreationForm(option)}
        onChange={(_e, option) => openTicketCreationForm(option)}
      />
    </Box>
  );

  const buildExportTable = (): { headers: string[]; rows: string[][] } | null => {
    const grid = dataGrid.current?.slickGrid;
    const dataView = dataGrid.current?.dataView;
    if (!grid || !dataView) return null;

    const visibleColumns = (grid.getColumns() as any[]).filter(
      (col: any) => col?.id && col.id !== "_checkbox_selector" && !String(col.id).startsWith("_"),
    );
    const defById = new Map<string, any>((columnDefinitions as any[]).map((c: any) => [String(c.id), c]));
    const items = ((dataView.getFilteredItems() as any[]) ?? []).filter(
      (it: any) => it && !it.__isDummy && !it.__group && !it.__groupTotals,
    );

    const headers = visibleColumns.map((col: any) => String(defById.get(String(col.id))?.name ?? col.name ?? col.id));
    const rows = items.map((item: any) =>
      visibleColumns.map((col: any) => {
        const def: any = defById.get(String(col.id));
        let value: unknown;
        if (def?._excelValueGetter) value = def._excelValueGetter(item);
        else if (def?._valueGetter) value = def._valueGetter(item);
        else value = _.get(item, def?.field ?? col.field);
        return value == null ? "" : String(value);
      }),
    );
    return { headers, rows };
  };

  const exportFileName = (ext: string) =>
    `SLA_Report_${String(convertDate(Date.now(), "NormalDate")).replace(/\//g, "-")}.${ext}`;

  const handleExportExcel = async () => {
    if (exportingRef.current) return;
    const table = buildExportTable();
    if (!table || table.rows.length === 0) {
      dispatch(showToastNotification({ content: t("No tickets to export"), type: TOAST_TYPE_ERROR }));
      return;
    }
    exportingRef.current = true;
    setIsExporting(true);
    try {
      const XLSX = await import("xlsx");
      const worksheet = XLSX.utils.aoa_to_sheet([table.headers, ...table.rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "SLA Report");
      XLSX.writeFile(workbook, exportFileName("xlsx"));
    } catch {
      dispatch(showToastNotification({ content: t("Failed to export report"), type: TOAST_TYPE_ERROR }));
    } finally {
      exportingRef.current = false;
      setIsExporting(false);
    }
  };

  // The SLA report PDF is generated server-side by the pdfExport queue (Puppeteer) rather than in
  // the browser, so it is a proper formatted report. We pass the current getList scope + the grid's
  // live column filters so the rendered report covers exactly the tickets the user is viewing.
  const handleExportPdf = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setIsExporting(true);
    try {
      const filterPredicates = dataGrid.current?.gridState?.filterPredicates;
      const hasFilters =
        filterPredicates && typeof filterPredicates === "object" && Object.keys(filterPredicates).length > 0;
      await exportPdf({
        supportReport: true,
        supportCreatedBy: createdByFilter,
        supportWhere: hasFilters ? JSON.stringify([filterPredicates]) : undefined,
        exportType: EXPORT_TYPE.PDF,
      });
    } finally {
      exportingRef.current = false;
      setIsExporting(false);
    }
  };

  const exportMenuItems: CreateTicketMenuItem[] = [
    { id: "excel", text: t("Export to Excel"), iconName: "ExcelExportIcon" },
    { id: "pdf", text: t("Export to PDF"), iconName: "PdfExport" },
  ];

  const exportDropdown = () => (
    <Box className="ms-4" data-testid="exportSlaBtn">
      <SelectDropDown<CreateTicketMenuItem>
        items={exportMenuItems}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        disabled={isExporting}
        startIcon={<IconComponent name="Export" width="1rem" height="1rem" />}
        placeholder={t("Export Report")}
        sx={{ "& .MuiButton-startIcon": { margin: 0 } }}
        renderOption={(option) => (
          <Box className="flex gap-2 items-center">
            <IconComponent name={option.iconName} width="1.25rem" height="1.25rem" />
            <Text>{option.text}</Text>
          </Box>
        )}
        select={(args) => {
          const id = args.item.properties.id;
          if (id === "excel") handleExportExcel();
          else if (id === "pdf") handleExportPdf();
        }}
      />
    </Box>
  );

  const toolBar = [
    {
      id: "createTicket",
      template: () => createTicketDropdown(),
    },
    ...(isCurrentUserWhiteListed
      ? [
          {
            id: "changeType",
            template: () => (
              <Box className="ms-4" data-testid="changeTypeBtn">
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                  disabled={!selectedTicket}
                  onClick={openChangeTypeModal}
                >
                  <span className="flex gap-2 items-center">
                    <IconComponent name="ChangeTicketType" width="1rem" height="1rem" />
                    <Text>{t("Change Type")}</Text>
                  </span>
                </ButtonComponent>
              </Box>
            ),
          },
          {
            id: "exportSla",
            template: () => exportDropdown(),
          },
        ]
      : []),
  ];

  const recordClick = (args: any) => {
    const rowIndex = args.detail.args.row;
    const clickedRow = dataGrid.current.slickGrid.getDataItem(rowIndex);
    if (args.detail.args.cell != 0) {
      navigate(
        getAllNotificationPageUrl({
          section: "support",
          ticketId: clickedRow._id,
        }),
      );
    }
  };

  const handleSelectionChange = (args: any) => {
    const rows: any[] = args?.detail?.args?.rows ?? [];
    if (rows.length === 1) {
      const ticket = dataGrid.current?.slickGrid?.getDataItem(rows[0]);
      setSelectedTicket(ticket ?? null);
    } else {
      setSelectedTicket(null);
    }
  };

  const openChangeTypeModal = () => {
    if (!selectedTicket) return;
    setShowChangeTypeModal(true);
  };

  const handleChangeTypeConfirm = async (payload: ChangeTypePayload) => {
    setChangeTypeLoading(true);
    try {
      const res = (await jiraController({
        value: {
          action: "changeType",
          ticketId: payload.ticketId,
          newIssueType: payload.newIssueType,
          newStatus: payload.newStatus,
          ...(payload.impact ? { impact: payload.impact } : {}),
          ...(payload.severity ? { severity: payload.severity } : {}),
        },
      })) as any;
      if (res?.data?.status) {
        dispatch(
          showToastNotification({
            content: t("Ticket type changed successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        setShowChangeTypeModal(false);
        setSelectedTicket(null);
        refreshGrid();
      } else {
        dispatch(
          showToastNotification({
            content: res?.data?.errorMessage || t("Failed to change ticket type"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch {
      dispatch(
        showToastNotification({
          content: t("Failed to change ticket type"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } finally {
      setChangeTypeLoading(false);
    }
  };

  const getJiraGrid = () => (
    <Box className="px-4 pb-4 h-full">
      <SlickGrid1
        ref={dataGrid}
        columnDefinitions={columnDefinitions}
        remoteDataSource={jiraTicketsUrl}
        query={query}
        onClick={recordClick}
        onSelectedRowIdsChanged={handleSelectionChange}
        toolbar={toolBar}
        showToolbarBorder
        enableSortingOption
        enableFilteringOption
        enableGroupingOption
        allowGrouping
        enableColumnChooser
        gridOptions={{ showPreHeaderPanel: true }}
        enableColumnPicker={false}
        triggerUpdateCurrentView={viewTrigger}
        enableViews={isCurrentUserWhiteListed}
        {...(isCurrentUserWhiteListed && {
          viewsOptions: {
            headerName: "Views",
            name: "View",
            resourceType: "JIRA",
            selectedLayoutKey: "JIRA_SELECTED_LAYOUT",
            fetchParams: {
              portalId: portalData?._id,
            },
          },
        })}
        onClickLayout={handleClickLayout}
      />
    </Box>
  );

  const modalContent = () => {
    if (showCreationLoader) {
      return (
        <Box className="flex justify-center flex-col items-center gap-5 h-full w-full">
          <Text className="font-semibold text-center">
            {t("It may take a few seconds to create ticket and add attachments if any")}
          </Text>
          <CircularProgress />
        </Box>
      );
    }
    return (
      <Box className="flex flex-col p-4 gap-5 w-full h-full overflow-auto">
        <Box className="flex gap-4 font-semibold flex-col ">
          {_.get(ticketHeaderInfo, `${showTicketCreationForm.type}.header`)}
        </Box>
        <Box className="flex overflow-auto flex-col gap-8 w-full h-full">
          {ticketCreationFields.fields.map(
            (data: any) =>
              data.heading.allowIn.includes(showTicketCreationForm.type) && (
                <Box key={data.heading.text} className="flex flex-col gap-2 w-full">
                  <Box className="font-semibold">{data.heading.text}</Box>
                  {data.subHeading?.allowIn.includes(showTicketCreationForm.type) && (
                    <Box className="text-sm">{data.subHeading.text}</Box>
                  )}
                  {data.component.allowIn.includes(showTicketCreationForm.type) && (
                    <Box className="flex">{data.component.component}</Box>
                  )}
                </Box>
              ),
          )}
        </Box>
      </Box>
    );
  };

  const getHeaderTitle = () => {
    if (_.get(ticketHeaderInfo, `${showTicketCreationForm.type}`)) {
      return (
        <Box className="flex items-center gap-2">
          <IconComponent
            name={_.get(ticketHeaderInfo, `${showTicketCreationForm.type}.svgName`)}
            width="2rem"
            height="2rem"
          />
          {_.get(ticketHeaderInfo, `${showTicketCreationForm.type}.title`)}
        </Box>
      );
    }
  };

  const footerContent = () => {
    return (
      <ButtonComponent
        disabled={isDisabledSaveButton || !canRaiseSupportTicket}
        onClick={handleSaveBugDetails}
        loading={showCreationLoader}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
      >
        <Text>{t("Save")}</Text>
      </ButtonComponent>
    );
  };

  const handleFilterChange = (args: any) => {
    const value = args.target.value;

    if (!value || value === createdByFilter) return;

    searchParams.set("createdBy", value);
    setCreatedByFilter(value);
    setQuery({ action: "getList", createdBy: value, requiresCounts: true });
    setViewTrigger({ queryConditions: { createdBy: value } });

    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const displayTicketsFilter = () => {
    const reportedFilter = getReportedFilter(whiteListUsersForJiraTickets ?? [], userDetails, t);
    return (
      <Box className="flex px-8 py-4 items-center gap-4 w-full">
        <Text className="text-base">{t("Display tickets")}</Text>
        <Select value={createdByFilter} name="createdBy" onChange={handleFilterChange} sx={{ height: "2.1875rem" }}>
          {reportedFilter.map((val: any) => (
            <MenuItem key={val.value} value={val.value}>
              {val.name}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  return (
    <Box key="jiraTickets" className="flex flex-col w-full h-full">
      {createdByFilter && displayTicketsFilter()}
      {columnDefinitions.length > 0 && getJiraGrid()}
      <ModalStateDialogComponent
        height="80%"
        width="70%"
        isVisible={showTicketCreationForm.status}
        onCancel={closeTicketCreationModal}
        footerContent={footerContent()}
        modelContent={modalContent()}
        headerTitle={getHeaderTitle()}
      />
      <ChangeTicketTypeModal
        isVisible={showChangeTypeModal}
        ticket={selectedTicket}
        onCancel={() => setShowChangeTypeModal(false)}
        onConfirm={handleChangeTypeConfirm}
        loading={changeTypeLoading}
      />
    </Box>
  );
};

export default JiraTickets;
